import { offlineDb, SyncOperation } from '@/db/offlineDb';
import { supabase } from '@/integrations/supabase/client';
import { networkService } from './network.service';
import { syncLock } from '@/services/sync/syncLock';
import OfflineLogger from '@/utils/offlineLogger';
import { enhancedImageCacheService } from './offline/image-cache-enhanced.service';
import { sanitizeQueueItemCategory } from '@/services/sync/sanitizeQueueItemCategory';
import { getValidUserId } from '@/utils/validation';
import { getCachedUserId } from '@/services/auth/sessionCache';
import {
  resolveDuplicateProductConflict,
  resolveDuplicateProductPreemptively
} from '@/services/sync/productDuplicateResolution';
import { resolvePrimaryListConflict } from '@/services/sync/primaryListDuplicateResolution';
import { validateQuantity } from '@/utils/security';
import { serializeError } from '@/utils/serializeError';

const DEFAULT_SANITIZED_QUANTITY = (() => {
  const result = validateQuantity('1');
  return result.isValid ? result.sanitized : '1';
})();

const normalizeQuantityValue = (value?: string | number | null): string => {
  const result = validateQuantity(value ?? DEFAULT_SANITIZED_QUANTITY);
  if (result.isValid) {
    return result.sanitized;
  }
  return DEFAULT_SANITIZED_QUANTITY;
};

class PendingUserIdError extends Error {
  constructor(message = 'User ID non disponibile per la sincronizzazione') {
    super(message);
    this.name = 'PendingUserIdError';
  }
}

class SyncService {
  private syncIntervalId: NodeJS.Timeout | null = null;
  private syncListeners: Array<() => void> = [];
  
  // ✅ FIX: Flag per disabilitare il sync automatico periodico durante l'initial sync
  private periodicSyncDisabled = false;

  constructor() {
    // ✅ FIX: Non avvia il sync periodico automaticamente per evitare conflitti
    OfflineLogger.info('SyncService', 'Constructor - periodic sync disabled to avoid conflicts with initial sync');
  }

  // ✅ NUOVO: Metodo per disabilitare temporaneamente il sync periodico
  disablePeriodicSync() {
    OfflineLogger.info('SyncService', 'Disabling periodic sync');
    this.periodicSyncDisabled = true;
    this.stopSyncInterval();
  }

  // ✅ NUOVO: Metodo per riabilitare il sync periodico
  enablePeriodicSync() {
    OfflineLogger.info('SyncService', 'Enabling periodic sync');
    this.periodicSyncDisabled = false;
    this.setupPeriodicSync();
  }

  private setupPeriodicSync() {
    if (this.periodicSyncDisabled) {
      OfflineLogger.info('SyncService', 'Periodic sync disabled, skipping setup');
      return;
    }

    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
    }
    
    this.syncIntervalId = setInterval(async () => {
      if (networkService.isOnline() && !syncLock.isSyncing && !this.periodicSyncDisabled) {
        try {
          OfflineLogger.info('SyncService', 'Periodic sync starting');
          await this.syncData();
        } catch (error) {
          OfflineLogger.error('SyncService.periodicSync', error);
        }
      }
    }, 30000); // Sync ogni 30 secondi
  }

  onSyncComplete(callback: () => void): () => void {
    this.syncListeners.push(callback);
    return () => {
      const index = this.syncListeners.indexOf(callback);
      if (index > -1) {
        this.syncListeners.splice(index, 1);
      }
    };
  }

  private async notifySyncComplete() {
    this.syncListeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        OfflineLogger.error('SyncService.notifySyncComplete', error);
      }
    });

    try {
      const pending = await this.getPendingOperationsCount();
      window.dispatchEvent(
        new CustomEvent('syncCompleted', { detail: { pendingOperations: pending } })
      );
      window.dispatchEvent(
        new CustomEvent('syncStatusChanged', { detail: { pendingOperations: pending } })
      );
    } catch (error) {
      OfflineLogger.error('SyncService.notifySyncComplete', error);
    }
  }

  startSyncInterval() {
    if (!this.syncIntervalId && !this.periodicSyncDisabled) {
      this.setupPeriodicSync();
    }
  }

  stopSyncInterval() {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
    }
  }

  async addToSyncQueue(
    entityType: string,
    operation: 'create' | 'update' | 'delete',
    entityId: string,
    entityData?: any
  ): Promise<boolean> {
    try {
      // ✅ FIX: Pulisci i dati prima di aggiungerli alla coda
      const cleanData = this.cleanDataForSync(entityData);
      
      const queueItem: Omit<SyncOperation, 'id'> = {
        entityType,
        entityId,
        operation,
        operation_type: operation,
        data: cleanData,
        timestamp: Date.now(),
        retryCount: 0,
        status: 'pending'
      };

      await offlineDb.syncQueue.add(queueItem);
      OfflineLogger.info('SyncService.addToSyncQueue', `Added ${operation} operation for ${entityType}:${entityId}`);
      return true;
    } catch (error) {
      OfflineLogger.error('SyncService.addToSyncQueue', error);
      return false;
    }
  }

  // ✅ FIX: Nuovo metodo per pulire i dati prima della sincronizzazione
  private cleanDataForSync(data: any): any {
    if (!data) return data;

    const cleanData = { ...data };

    // Rimuovi i campi di sincronizzazione che non esistono nel database remoto
    delete cleanData._syncStatus;
    delete cleanData._lastModified;

    // Converti stringhe vuote in NULL per evitare errori di validazione
    Object.keys(cleanData).forEach(key => {
      if (cleanData[key] === '') {
        cleanData[key] = null;
      }
    });

    if ('quantity' in cleanData && cleanData.quantity !== null && cleanData.quantity !== undefined) {
      cleanData.quantity = normalizeQuantityValue(cleanData.quantity as string | number | null);
    }

    return cleanData;
  }

  async syncData(userId?: string): Promise<void> {
    if (syncLock.isSyncing || !networkService.isOnline()) {
      return;
    }

    // Lock globale condiviso: evita che questo ciclo e quello di
    // SmartAutoSyncService processino la stessa coda in parallelo.
    await syncLock.runExclusive(async () => {
      try {
        OfflineLogger.info('SyncService', 'Starting syncData');
        await this.processSyncQueue();
        await this.notifySyncComplete();
      } catch (error) {
        OfflineLogger.error('SyncService.syncData', error);
      }
    });
  }

  async manualSync(userId?: string): Promise<void> {
    if (!networkService.isOnline()) {
      throw new Error('Cannot sync while offline');
    }

    try {
      // Su una sincronizzazione esplicita dell'utente, ridiamo una possibilità
      // agli item già marcati 'failed': altrimenti resterebbero bloccati per
      // sempre e nessun processore li riprenderebbe.
      await this.resetFailedOperations();
      await this.syncData(userId);
      OfflineLogger.info('SyncService.manualSync', 'Manual sync completed successfully');
    } catch (error) {
      OfflineLogger.error('SyncService.manualSync', error);
      throw error;
    }
  }

  /**
   * Reimposta a 'pending' gli item della coda marcati 'failed', azzerandone il
   * retry count, così che una sincronizzazione li riprocessi. Restituisce il
   * numero di item ripristinati.
   */
  async resetFailedOperations(): Promise<number> {
    try {
      const failed = await offlineDb.syncQueue
        .filter(item => item.status === 'failed')
        .toArray();

      if (!failed.length) {
        return 0;
      }

      await offlineDb.syncQueue.bulkPut(
        failed.map(item => ({ ...item, status: 'pending', retryCount: 0 }))
      );

      OfflineLogger.info(
        'SyncService.resetFailedOperations',
        `Reset ${failed.length} item falliti a pending per nuovo tentativo`
      );
      return failed.length;
    } catch (error) {
      OfflineLogger.error('SyncService.resetFailedOperations', error);
      return 0;
    }
  }

  /**
   * Elimina definitivamente dalla coda gli item marcati 'failed' (azione
   * "Scarta"): la modifica locale resta, ma non verrà più tentata la sync.
   */
  async clearFailedOperations(): Promise<number> {
    try {
      const failedKeys = await offlineDb.syncQueue
        .filter(item => item.status === 'failed')
        .primaryKeys();

      if (!failedKeys.length) {
        return 0;
      }

      await offlineDb.syncQueue.bulkDelete(failedKeys);
      OfflineLogger.info(
        'SyncService.clearFailedOperations',
        `Scartati ${failedKeys.length} item falliti dalla coda`
      );
      return failedKeys.length;
    } catch (error) {
      OfflineLogger.error('SyncService.clearFailedOperations', error);
      return 0;
    }
  }

  async getEntityCounts(entityKey: string, userId?: string): Promise<{ offlineCount: number; onlineCount: number }> {
    try {
      let offlineCount = 0;
      let onlineCount = 0;

      // Conta offline
      if (offlineDb[entityKey]) {
        if (userId) {
          offlineCount = await offlineDb[entityKey].where('user_id').equals(userId).count();
        } else {
          offlineCount = await offlineDb[entityKey].count();
        }
      }

      // Conta online se disponibile
      if (networkService.isOnline()) {
        const tableName = this.getTableName(entityKey);
        let query = supabase.from(tableName).select('*', { count: 'exact', head: true });
        
        if (userId) {
          query = query.eq('user_id', userId);
        }
        
        const { count } = await query;
        onlineCount = count || 0;
      }

      return { offlineCount, onlineCount };
    } catch (error) {
      OfflineLogger.error('SyncService.getEntityCounts', error);
      return { offlineCount: 0, onlineCount: 0 };
    }
  }

  async getEntityPendingCount(entityKey: string): Promise<number> {
    try {
      return await offlineDb.syncQueue.where('entityType').equals(entityKey).and(item => item.status === 'pending').count();
    } catch (error) {
      OfflineLogger.error('SyncService.getEntityPendingCount', error);
      return 0;
    }
  }

  async syncAndRefreshEntities(entityTypes: string[], userId?: string): Promise<void> {
    for (const entityType of entityTypes) {
      try {
        if (userId) {
          await this.syncEntity(entityType, userId);
        }
      } catch (error) {
        OfflineLogger.error(`SyncService.syncAndRefreshEntities.${entityType}`, error);
      }
    }
  }

  async syncPendingOperations(): Promise<void> {
    if (!networkService.isOnline()) {
      OfflineLogger.info('SyncService.syncPendingOperations', 'Skipping - offline mode');
      return;
    }

    try {
      await this.processSyncQueue();
      OfflineLogger.info('SyncService.syncPendingOperations', 'Pending operations synced');
      await this.notifySyncComplete();
    } catch (error) {
      OfflineLogger.error('SyncService.syncPendingOperations', error);
      throw error;
    }
  }

  private async sanitizeQueueCategories(): Promise<void> {
    try {
      const queueItems = await offlineDb.syncQueue
        .where('status')
        .notEqual('failed')
        .toArray();

      for (const item of queueItems) {
        await sanitizeQueueItemCategory(item);
      }
    } catch (error) {
      OfflineLogger.error('SyncService.sanitizeQueueCategories', error);
    }
  }

  private async processSyncQueue(): Promise<void> {
    try {
      await this.realignOfflineUserReferences();
      await this.sanitizeQueueCategories();
      const queueItems = await offlineDb.syncQueue
        .orderBy('timestamp')
        .filter(item => item.status !== 'failed')
        .toArray();
      OfflineLogger.info('SyncService.processSyncQueue', `Processing ${queueItems.length} items in sync queue`);
      
      for (const item of queueItems) {
        try {
          await this.processSyncItem(item);
          await offlineDb.syncQueue.delete(item.id!);
        } catch (error) {
          if (error instanceof PendingUserIdError) {
            OfflineLogger.info(
              'SyncService.processSyncQueue',
              `Operazione in attesa di user_id valido: ${item.entityType}:${item.entityId}`
            );

            await offlineDb.syncQueue.update(item.id!, {
              status: 'pending',
              errorDetail: error.message
            });
            continue;
          }

          OfflineLogger.error(`SyncService.processSyncItem:${item.entityType}:${item.operation}`, error);

          // Incrementa retry count
          const nextRetry = (item.retryCount || 0) + 1;
          await offlineDb.syncQueue.update(item.id!, {
            retryCount: nextRetry,
            status: nextRetry >= 3 ? 'failed' : item.status,
            errorDetail: serializeError(error)
          });

          // Item esaurito: NON eliminare (eviti perdita silenziosa della
          // modifica). Resta come 'failed' e potrà essere ritentato dall'utente
          // tramite una sincronizzazione manuale (vedi resetFailedOperations).
          if (nextRetry >= 3) {
            OfflineLogger.error(
              'SyncService.processSyncItem',
              `Item kept as failed after 3 retries (retry manuale disponibile): ${item.entityType}:${item.entityId} — ${serializeError(error)}`
            );
          }
        }
      }
    } catch (error) {
      OfflineLogger.error('SyncService.processSyncQueue', error);
    }
  }

  private async processSyncItem(item: SyncOperation): Promise<void> {
    switch (item.operation) {
      case 'create':
        await this.syncCreate(item);
        break;
      case 'update':
        await this.syncUpdate(item);
        break;
      case 'delete':
        await this.syncDelete(item);
        break;
    }
  }

  private async syncCreate(item: SyncOperation): Promise<void> {
    try {
      const tableName = this.getTableName(item.entityType);

      // ✅ FIX: Pulisci sempre i dati prima dell'invio
      const cleanData = this.cleanDataForSync(item.data);

      const isShoppingListEntity = item.entityType === 'shoppingLists' || item.entityType === 'shopping_lists';

      if (cleanData?.user_id === 'offline-user') {
        const currentUserId = await this.resolveCurrentUserId();

        if (!currentUserId) {
          throw new PendingUserIdError('User ID non ancora disponibile per la sincronizzazione');
        }

        cleanData.user_id = currentUserId;

        if (item.data) {
          item.data = { ...item.data, user_id: currentUserId };
          if (item.id) {
            await offlineDb.syncQueue.update(item.id, { data: item.data, status: 'pending' });
          }
        }

        if (isShoppingListEntity) {
          await offlineDb.shoppingLists.update(item.entityId, { user_id: currentUserId });
        }
      }

      // ✅ FIX: Per shopping_categories, controlla se esiste già prima di inserire
      if (item.entityType === 'shopping_categories') {
        const { data: existing } = await supabase
          .from(tableName)
          .select('id')
          .eq('list_id', cleanData.list_id)
          .eq('category_id', cleanData.category_id)
          .maybeSingle();
          
        if (existing) {
          OfflineLogger.info('SyncService.syncCreate', `Shopping category already exists, skipping: ${item.entityId}`);
          await this.updateLocalSyncStatus(item.entityType, item.entityId, 'synced');
          return;
        }
      }
      
      if (item.entityType === 'products') {
        const fallbackUserId = cleanData?.user_id ?? (await this.resolveCurrentUserId());
        const preemptiveResolution = await resolveDuplicateProductPreemptively({
          cleanData,
          entityId: item.entityId,
          queueItemId: item.id,
          tableName,
          loggerContext: 'SyncService.syncCreate',
          fallbackUserId
        });

        if (preemptiveResolution.resolved) {
          const resolvedId = preemptiveResolution.resolvedProductId ?? item.entityId;
          await this.updateLocalSyncStatus(item.entityType, resolvedId, 'synced');
          OfflineLogger.info(
            'SyncService.syncCreate',
            `Rilevato duplicato per ${item.entityType}:${item.entityId} associandolo al record ${resolvedId} prima dell'upsert`
          );
          return;
        }
      }

      // ✅ FIX #1393: Evita la violazione del vincolo univoco parziale
      // unique_primary_list_per_user_idx (una sola lista primaria per utente).
      // Una lista primaria creata offline può collidere con quella già presente
      // sul server (id diverso): la degradiamo a non-primaria prima dell'upsert.
      if (isShoppingListEntity) {
        const fallbackUserId = cleanData?.user_id ?? (await this.resolveCurrentUserId());
        await resolvePrimaryListConflict({
          cleanData,
          entityId: item.entityId,
          tableName,
          loggerContext: 'SyncService.syncCreate',
          fallbackUserId
        });
      }

      // ✅ FIX: Per altre entità, usa upsert quando possibile
      try {
        if (['categories', 'products', 'shops', 'shoppingLists', 'recurringProducts'].includes(item.entityType)) {
          const { error } = await supabase
            .from(tableName)
            .upsert([cleanData], { onConflict: 'id' });

          if (error) {
            throw error;
          }
        } else {
          const { error } = await supabase
            .from(tableName)
            .insert([cleanData]);

          if (error) {
            throw error;
          }
        }
      } catch (error) {
        // ✅ FIX #1393: Safety net per race residue sull'indice parziale della
        // lista primaria. Se l'upsert fallisce ancora con 23505 su una lista
        // primaria, ritenta degradandola a non-primaria invece di rilanciare.
        if (
          isShoppingListEntity &&
          cleanData?.is_primary === true &&
          (error as { code?: string }).code === '23505'
        ) {
          cleanData.is_primary = false;
          try {
            await offlineDb.shoppingLists.update(item.entityId, {
              is_primary: false,
              _lastModified: Date.now()
            });
          } catch (localError) {
            OfflineLogger.warn(
              'SyncService.syncCreate',
              `Impossibile degradare la lista locale ${item.entityId}: ${serializeError(localError)}`
            );
          }

          const { error: retryError } = await supabase
            .from(tableName)
            .upsert([cleanData], { onConflict: 'id' });

          if (retryError) {
            throw retryError;
          }

          await this.updateLocalSyncStatus(item.entityType, item.entityId, 'synced');
          OfflineLogger.info(
            'SyncService.syncCreate',
            `Lista primaria ${item.entityId} sincronizzata come non-primaria dopo conflitto sul vincolo univoco`
          );
          return;
        }

        if (item.entityType === 'products' && (error as { code?: string }).code === '23505') {
          const fallbackUserId = cleanData?.user_id ?? (await this.resolveCurrentUserId());
          const resolution = await resolveDuplicateProductConflict({
            cleanData,
            entityId: item.entityId,
            queueItemId: item.id,
            tableName,
            loggerContext: 'SyncService.syncCreate',
            fallbackUserId
          });

          if (resolution.resolved) {
            const resolvedId = resolution.resolvedProductId ?? item.entityId;
            await this.updateLocalSyncStatus(item.entityType, resolvedId, 'synced');
            OfflineLogger.info(
              'SyncService.syncCreate',
              `Risolto conflitto di duplicato per ${item.entityType}:${item.entityId} associandolo al record ${resolvedId}`
            );
            return;
          }
        }

        // ✅ FIX #1502: Safety-net per gli shopping item già presenti sul server.
        // Una create duplicata in coda (op ripetuta, retry dopo un successo
        // parziale, oppure echo Realtime) viola shopping_items_pkey con codice
        // 23505: la riga esiste già con lo stesso UUID generato lato client,
        // quindi è idempotente trattarla come già sincronizzata. Non usiamo
        // upsert (a differenza di liste/prodotti) per non sovrascrivere eventuali
        // modifiche server-side (es. checked, position) con i dati di create
        // ormai obsoleti: le modifiche locali viaggiano come op 'update' separate.
        if (
          (item.entityType === 'shoppingItems' || item.entityType === 'items') &&
          (error as { code?: string }).code === '23505'
        ) {
          await this.updateLocalSyncStatus(item.entityType, item.entityId, 'synced');
          OfflineLogger.info(
            'SyncService.syncCreate',
            `Shopping item ${item.entityId} già presente sul server (23505): trattato come già sincronizzato`
          );
          return;
        }

        throw error;
      }

      // Aggiorna stato locale a synced
      await this.updateLocalSyncStatus(item.entityType, item.entityId, 'synced');
      OfflineLogger.info('SyncService.syncCreate', `Successfully created/upserted ${item.entityType}:${item.entityId}`);
    } catch (error) {
      if ((error as { code?: string }).code === '23503') {
        OfflineLogger.warn(
          'SyncService.syncCreate',
          `Violazione di vincolo FK per ${item.entityType}:${item.entityId}, tentativo di riallineamento user_id`
        );
        await this.realignOfflineUserReferences();
        throw new PendingUserIdError('Attesa riallineamento user_id per completare la sincronizzazione');
      }

      OfflineLogger.error(
        'SyncService.syncCreate',
        `Error creating record in ${item.entityType} with id ${item.entityId}: ${serializeError(error)}`
      );
      throw error;
    }
  }

  private async resolveCurrentUserId(): Promise<string | null> {
    try {
      return getValidUserId() ?? (await getCachedUserId());
    } catch (error) {
      OfflineLogger.error('SyncService.resolveCurrentUserId', error);
      return null;
    }
  }

  private async realignOfflineUserReferences(): Promise<string | null> {
    const currentUserId = await this.resolveCurrentUserId();

    if (!currentUserId) {
      return null;
    }

    try {
      const offlineLists = await offlineDb.shoppingLists
        .where('user_id')
        .equals('offline-user')
        .toArray();

      if (offlineLists.length) {
        await offlineDb.shoppingLists.bulkPut(
          offlineLists.map(list => ({ ...list, user_id: currentUserId }))
        );
      }

      const queueItems = await offlineDb.syncQueue
        .filter(
          operation =>
            (operation.entityType === 'shoppingLists' || operation.entityType === 'shopping_lists') &&
            operation.data?.user_id === 'offline-user'
        )
        .toArray();

      for (const queueItem of queueItems) {
        await offlineDb.syncQueue.update(queueItem.id!, {
          data: { ...queueItem.data, user_id: currentUserId },
          status: 'pending'
        });
      }
    } catch (error) {
      OfflineLogger.error('SyncService.realignOfflineUserReferences', error);
    }

    return currentUserId;
  }

  private async syncUpdate(item: SyncOperation): Promise<void> {
    try {
      const tableName = this.getTableName(item.entityType);
      
      // ✅ FIX: Pulisci sempre i dati prima dell'invio
      const cleanData = this.cleanDataForSync(item.data);
      
      // ✅ FIX: Per shopping_categories, usa la chiave composta
      if (item.entityType === 'shopping_categories') {
        const { error } = await supabase
          .from(tableName)
          .update(cleanData)
          .eq('list_id', cleanData.list_id)
          .eq('category_id', cleanData.category_id);

        if (error) {
          throw error;
        }
      } else {
        const { error } = await supabase
          .from(tableName)
          .update(cleanData)
          .eq('id', item.entityId);

        if (error) {
          throw error;
        }
      }

      // Aggiorna stato locale a synced
      await this.updateLocalSyncStatus(item.entityType, item.entityId, 'synced');
      OfflineLogger.info('SyncService.syncUpdate', `Successfully updated ${item.entityType}:${item.entityId}`);
    } catch (error) {
      OfflineLogger.error('SyncService.syncUpdate', error);
      throw error;
    }
  }

  private async syncDelete(item: SyncOperation): Promise<void> {
    try {
      const tableName = this.getTableName(item.entityType);
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', item.entityId);

      if (error) {
        throw error;
      }

      // Rimuovi dalla cache locale
      await this.removeFromLocalCache(item.entityType, item.entityId);
      OfflineLogger.info('SyncService.syncDelete', `Successfully deleted ${item.entityType}:${item.entityId}`);
    } catch (error) {
      OfflineLogger.error('SyncService.syncDelete', error);
      throw error;
    }
  }

  private getTableName(entityType: string): string {
    // ✅ FIX: Mapping corretto e completo dei nomi delle tabelle
    const tableMapping: Record<string, string> = {
      'categories': 'user_categories',
      'products': 'user_products',
      'lists': 'shopping_lists',
      'shoppingLists': 'shopping_lists',
      'items': 'shopping_items',
      'shoppingItems': 'shopping_items',
      'shops': 'user_shops',
      'purchaseHistory': 'shopping_history',
      'recurringProducts': 'recurring_products',
      'images': 'user_products',
      'shopping_categories': 'shopping_categories', // ✅ FIX: Aggiunto mapping mancante
      'tutorialPreferences': 'profiles'
    };

    return tableMapping[entityType] || entityType;
  }

  private async updateLocalSyncStatus(entityType: string, entityId: string, status: 'synced' | 'pending' | 'deleted'): Promise<void> {
    try {
      switch (entityType) {
        case 'categories':
          await offlineDb.categories.update(entityId, { _syncStatus: status });
          break;
        case 'products':
          await offlineDb.products.update(entityId, { _syncStatus: status });
          break;
        case 'shops':
          await offlineDb.shops.update(entityId, { _syncStatus: status });
          break;
        case 'shoppingLists':
          await offlineDb.shoppingLists.update(entityId, { _syncStatus: status });
          break;
        case 'shoppingItems':
          await offlineDb.shoppingItems.update(entityId, { _syncStatus: status });
          break;
        case 'recurringProducts':
          await offlineDb.recurringProducts.update(entityId, { _syncStatus: status });
          break;
        case 'tutorialPreferences':
          await offlineDb.tutorialPreferences.update(entityId, { _syncStatus: status });
          break;
        // ✅ FIX: Non gestiamo shopping_categories in locale perché non ha una tabella offline dedicata
        case 'shopping_categories':
          // Le shopping_categories vengono gestite dinamicamente, non le salviamo offline
          break;
        // Aggiungi altri tipi di entità quando necessario
      }
    } catch (error) {
      OfflineLogger.error('SyncService.updateLocalSyncStatus', error);
    }
  }

  private async removeFromLocalCache(entityType: string, entityId: string): Promise<void> {
    try {
      switch (entityType) {
        case 'categories':
          await offlineDb.categories.delete(entityId);
          break;
        case 'products':
          await offlineDb.products.delete(entityId);
          break;
        case 'shops':
          await offlineDb.shops.delete(entityId);
          break;
        case 'shoppingLists':
          await offlineDb.shoppingLists.delete(entityId);
          break;
        case 'shoppingItems':
          await offlineDb.shoppingItems.delete(entityId);
          break;
        case 'recurringProducts':
          await offlineDb.recurringProducts.delete(entityId);
          break;
        case 'tutorialPreferences':
          await offlineDb.tutorialPreferences.delete(entityId);
          break;
        // ✅ FIX: Non gestiamo shopping_categories in locale
        case 'shopping_categories':
          // Le shopping_categories vengono gestite dinamicamente
          break;
        // Aggiungi altri tipi di entità quando necessario
      }
    } catch (error) {
      OfflineLogger.error('SyncService.removeFromLocalCache', error);
    }
  }

  async syncEntity(entityType: string, userId: string): Promise<void> {
    if (!networkService.isOnline()) {
      OfflineLogger.info('SyncService.syncEntity', `Skipping ${entityType} sync - offline mode`);
      return;
    }

    try {
      OfflineLogger.info('SyncService.syncEntity', `Starting ${entityType} sync for user ${userId.substring(0, 8)}`);
      
      const tableName = this.getTableName(entityType);

      // ✅ FIX: Per shopping_categories, non sincronizziamo perché vengono gestite dinamicamente
      if (entityType === 'shopping_categories') {
        OfflineLogger.info('SyncService.syncEntity', 'Skipping shopping_categories sync - managed dynamically');
        return;
      }

      // ✅ NEW: gestione sincronizzazione immagini tramite enhancedImageCacheService
      if (entityType === 'images') {
        const { data: imageData, error: imageError } = await supabase
          .from(tableName)
          .select('id, photo')
          .eq('user_id', userId)
          .not('photo', 'is', null);

        if (imageError) {
          throw imageError;
        }

        if (imageData && imageData.length > 0) {
          await enhancedImageCacheService.preloadImages(imageData);
          OfflineLogger.info('SyncService.syncEntity', `Cached ${imageData.length} images for offline use`);
        } else {
          OfflineLogger.info('SyncService.syncEntity', 'No images found on server for user');
        }

        window.dispatchEvent(new CustomEvent(`${entityType}SyncCompleted`, {
          detail: { [entityType]: imageData, timestamp: Date.now(), source: 'sync-service' }
        }));

        return;
      }
      
      // Scarica dati dal server
      const { data: serverData, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      // ✅ FIX: Usa bulkPut invece di bulkAdd per evitare errori di chiavi duplicate
      if (serverData && serverData.length > 0) {
        const dataWithSync = serverData.map(item => ({
          ...item,
          _syncStatus: 'synced' as const,
          _lastModified: Date.now()
        }));

        switch (entityType) {
          case 'categories':
            // Prima elimina le categorie esistenti per l'utente
            await offlineDb.categories.where('user_id').equals(userId).delete();
            // Poi inserisci i nuovi dati
            await offlineDb.categories.bulkPut(dataWithSync);
            break;
          case 'products':
            // Elimina i prodotti esistenti per l'utente e inserisci i nuovi
            await offlineDb.products.where('user_id').equals(userId).delete();
            await offlineDb.products.bulkPut(dataWithSync);
            break;
          case 'shoppingLists':
            // Prima elimina le liste esistenti per l'utente
            await offlineDb.shoppingLists.where('user_id').equals(userId).delete();
            // Poi inserisci i nuovi dati
            await offlineDb.shoppingLists.bulkPut(dataWithSync);
            break;
          case 'shoppingItems':
            // Per gli item non c'è user_id diretto, quindi elimina tutti e reinserisci
            await offlineDb.shoppingItems.clear();
            await offlineDb.shoppingItems.bulkPut(dataWithSync);
            break;
          case 'recurringProducts':
            // Prima elimina i prodotti ricorrenti esistenti per l'utente
            await offlineDb.recurringProducts.where('user_id').equals(userId).delete();
            // Poi inserisci i nuovi dati
            await offlineDb.recurringProducts.bulkPut(dataWithSync);
            break;
          case 'shops':
            // Elimina i negozi esistenti per l'utente e inserisci i nuovi
            await offlineDb.shops.where('user_id').equals(userId).delete();
            await offlineDb.shops.bulkPut(dataWithSync);
            break;
          // Aggiungi altri tipi quando necessario
        }

        OfflineLogger.info('SyncService.syncEntity', `Synced ${serverData.length} ${entityType} records`);
      } else {
        OfflineLogger.info('SyncService.syncEntity', `No ${entityType} records found on server for user`);
      }

      // Emetti evento di sincronizzazione completata
      window.dispatchEvent(new CustomEvent(`${entityType}SyncCompleted`, {
        detail: { [entityType]: serverData, timestamp: Date.now(), source: 'sync-service' }
      }));

    } catch (error) {
      OfflineLogger.error('SyncService.syncEntity', `Error syncing ${entityType}: ${error}`);
      throw error;
    }
  }

  async getPendingOperationsCount(): Promise<number> {
    try {
      // Esclude gli item 'failed': nessun processore li riprende
      // automaticamente, quindi non devono gonfiare il badge "da
      // sincronizzare" (che altrimenti risulterebbe impossibile da azzerare).
      return await offlineDb.syncQueue
        .filter(item => item.status !== 'failed')
        .count();
    } catch (error) {
      OfflineLogger.error('SyncService.getPendingOperationsCount', error);
      return 0;
    }
  }

  async getFailedOperationsCount(): Promise<number> {
    try {
      return await offlineDb.syncQueue
        .filter(item => item.status === 'failed')
        .count();
    } catch (error) {
      OfflineLogger.error('SyncService.getFailedOperationsCount', error);
      return 0;
    }
  }

  // ✅ FASE 2: Metodo unificato che usa il nuovo servizio
  async performUnifiedSync(entityNames?: string[]): Promise<any> {
    const { unifiedSyncService } = await import('./sync/UnifiedSyncService');
    return await unifiedSyncService.performUnifiedSync(entityNames);
  }

  // ✅ FASE 4: Check intelligente se sync è necessario
  async isEntitySyncNeeded(entityName: string, userId: string): Promise<boolean> {
    const { unifiedSyncService } = await import('./sync/UnifiedSyncService');
    return await unifiedSyncService.isEntitySyncNeeded(entityName, userId);
  }

  destroy() {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
    }
  }
}

export const syncService = new SyncService();
